import React, { useState } from 'react';
import ConfirmDestructiveDialog from '@/components/ConfirmDestructiveDialog';
import { InlineToggle } from '@/components/ui';

type Props = {
  onClose: () => void;
  onConfirm: (opts: { olderThanDays: number; status: string; alsoDeleteRelatedJobs: boolean }) => Promise<void>;
};

export default function CleanupDeliveriesConfirmModal({ onClose, onConfirm }: Props) {
  const [olderThanDays, setOlderThanDays] = useState(30);
  const [status, setStatus] = useState('SENT');
  const [alsoDeleteRelatedJobs, setAlsoDeleteRelatedJobs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function confirm() {
    setSubmitting(true);
    setError('');
    try {
      await onConfirm({ olderThanDays, status, alsoDeleteRelatedJobs });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Cleanup failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ConfirmDestructiveDialog
      title="Clear old notification history?"
      onClose={onClose}
      closeOnBackdrop={!submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" disabled={submitting} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" disabled={submitting} onClick={() => void confirm()}>
            {submitting ? 'Removing…' : 'Clear history'}
          </button>
        </>
      }
    >
      {error ? <div className="alert alert-error">{error}</div> : null}

      <p className="modal-lead">
        Remove delivery log rows older than the selected age. Discovered listing records are kept unless you
        separately remove related jobs.
      </p>

      <div className="admin-form-grid admin-form-grid--general">
        <label>
          Older than (days)
          <input
            type="number"
            min={1}
            max={3650}
            value={olderThanDays}
            onChange={(e) => setOlderThanDays(Number(e.target.value) || 30)}
            disabled={submitting}
          />
        </label>
        <label>
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)} disabled={submitting}>
            <option value="SENT">Sent only</option>
            <option value="FAILED">Failed only</option>
            <option value="SKIPPED">Skipped only</option>
          </select>
        </label>
        <InlineToggle
          checked={alsoDeleteRelatedJobs}
          onChange={setAlsoDeleteRelatedJobs}
          disabled={submitting}
          label="Also remove related jobs"
          className="admin-form-grid__full"
        />
      </div>
      <p className="modal-hint muted">Terminal send jobs only. Discovered listing rows are not deleted.</p>
    </ConfirmDestructiveDialog>
  );
}
